import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import emailCampaignService, { CampaignAnalytics } from '../../../services/emailCampaignService';

const EmailAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadAnalytics(id);
    }
  }, [id]);

  const loadAnalytics = async (campaignId: string) => {
    try {
      setLoading(true);
      const data = await emailCampaignService.getCampaignAnalytics(campaignId);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      alert('Failed to load campaign analytics');
      navigate('/creator/email-campaigns');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const StatCard: React.FC<{ label: string; value: string | number; subtext?: string; color?: string }> = ({
    label,
    value,
    subtext,
    color = 'blue',
  }) => (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <div className="text-sm text-gray-500 mb-1">{label}</div>
      <div className={`text-3xl font-bold text-${color}-600`}>{value}</div>
      {subtext && <div className="text-sm text-gray-500 mt-1">{subtext}</div>}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Campaign not found</p>
      </div>
    );
  }

  const { campaign, stats, sends } = analytics;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/creator/email-campaigns')}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center gap-2"
        >
          ← Back to Campaigns
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{campaign.name}</h1>
            <p className="text-gray-600 mt-1">{campaign.subject}</p>
            <p className="text-sm text-gray-500 mt-2">
              Sent on {formatDate(campaign.sentAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Sent"
          value={stats.totalSent}
          color="blue"
        />
        <StatCard
          label="Open Rate"
          value={`${stats.openRate.toFixed(1)}%`}
          subtext={`${stats.totalOpens} opens`}
          color="green"
        />
        <StatCard
          label="Click Rate"
          value={`${stats.clickRate.toFixed(1)}%`}
          subtext={`${stats.totalClicks} clicks`}
          color="purple"
        />
        <StatCard
          label="CTR"
          value={`${stats.clickThroughRate.toFixed(1)}%`}
          subtext="Click-through rate"
          color="indigo"
        />
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard
          label="Bounce Rate"
          value={`${stats.bounceRate.toFixed(1)}%`}
          subtext={`${stats.totalBounces} bounces`}
          color="yellow"
        />
        <StatCard
          label="Unsubscribes"
          value={stats.totalUnsubscribes}
          subtext={`${stats.unsubscribeRate.toFixed(2)}%`}
          color="red"
        />
        <StatCard
          label="Engagement Score"
          value={Math.round((stats.openRate + stats.clickRate) / 2)}
          subtext="Combined metric"
          color="blue"
        />
      </div>

      {/* Performance Chart Placeholder */}
      <div className="bg-white shadow-sm rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance Over Time</h2>
        <div className="bg-gray-50 rounded-lg h-64 flex items-center justify-center">
          <p className="text-gray-500">Chart visualization would go here</p>
        </div>
      </div>

      {/* Detailed Send Data */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Send Details ({sends.length} recipients)
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Sent At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Opened At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Clicked At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sends.slice(0, 50).map((send: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        send.status === 'CLICKED'
                          ? 'bg-purple-100 text-purple-800'
                          : send.status === 'OPENED'
                          ? 'bg-green-100 text-green-800'
                          : send.status === 'SENT'
                          ? 'bg-blue-100 text-blue-800'
                          : send.status === 'BOUNCED'
                          ? 'bg-yellow-100 text-yellow-800'
                          : send.status === 'UNSUBSCRIBED'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {send.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(send.sentAt)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {send.openedAt ? formatDate(send.openedAt) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {send.clickedAt ? formatDate(send.clickedAt) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sends.length > 50 && (
          <div className="mt-4 text-sm text-gray-500 text-center">
            Showing first 50 of {sends.length} recipients
          </div>
        )}
      </div>

      {/* Insights */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-3">Campaign Insights</h3>
        <div className="space-y-2 text-sm text-blue-800">
          {stats.openRate > 25 && (
            <div>✓ Great open rate! Your subject line is working well.</div>
          )}
          {stats.openRate < 15 && (
            <div>⚠ Low open rate. Try improving your subject line or send time.</div>
          )}
          {stats.clickRate > 2.5 && (
            <div>✓ Strong engagement! Your content resonates with readers.</div>
          )}
          {stats.clickRate < 1 && (
            <div>⚠ Low click rate. Consider adding more compelling calls-to-action.</div>
          )}
          {stats.bounceRate > 5 && (
            <div>⚠ High bounce rate. Consider cleaning up your patron list.</div>
          )}
          {stats.unsubscribeRate < 0.5 && (
            <div>✓ Low unsubscribe rate. Your patrons value your emails!</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailAnalyticsPage;
